import { useQuery } from '@tanstack/react-query';
import { problemText, useApi, useApiProblem } from '@/shared/api';
import { formatDateTime } from '@/shared/lib/format';
import { Collapsible } from './Collapsible';
import { StaleNote } from './StaleNote';
import { CONSENT_LABEL } from './guestLabels';

// DSGVO-Einwilligungen, zugeklappt. Append-only mit IP + User-Agent. Geladen wird erst beim
// Aufklappen – die Liste ist Nachweis, kein Arbeitsstand.
export function ConsentTrailCollapsible({ guestId }: { guestId: string }) {
  return (
    <Collapsible
      icon="Shield"
      title="Einwilligungen"
      summary="Zeitpunkt, Textfassung, IP und Browser"
    >
      <ConsentList guestId={guestId} />
    </Collapsible>
  );
}

function ConsentList({ guestId }: { guestId: string }) {
  const api = useApi();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'guest-consents', guestId],
    queryFn: () => api.system.guestConsents(guestId),
  });
  const problem = useApiProblem(error);
  if (isLoading) return <p className="text-ink-soft text-sm">Einwilligungen werden geladen …</p>;
  if (error && !data) {
    return (
      <p role="alert" className="text-ink-soft text-sm">
        {problemText(problem, 'Die Einwilligungen konnten nicht geladen werden.')}{' '}
        <button
          type="button"
          onClick={() => void refetch()}
          className="text-forest min-h-11 font-bold"
        >
          Erneut laden
        </button>
      </p>
    );
  }
  if (!data || data.length === 0)
    return <p className="text-ink-soft text-sm">Keine Einwilligungen erfasst.</p>;
  return (
    <>
      {error && <StaleNote onRetry={() => void refetch()} />}
      <ul className="space-y-3 text-sm">
        {data.map((c) => (
          <li key={c.id} className="border-hairline border-b pb-2 last:border-b-0">
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold">
                {CONSENT_LABEL[c.consentType] ?? c.consentType}{' '}
                <span className="text-ink-soft font-normal">(Fassung {c.textVersion})</span>
              </span>
              <span className="text-ink-soft text-xs">{formatDateTime(c.acceptedAt)}</span>
            </div>
            <div className="text-ink-soft mt-1 flex flex-wrap gap-x-3 text-xs">
              <span>IP {c.ipAddress || '—'}</span>
              <span className="truncate" title={c.userAgent}>
                Browser {c.userAgent || '—'}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
